import React, { useId, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { needsFullVideoPlan } from 'fym-shared/src/music24/studioThemes';
import type { CoverRedoItem, CoverRedoKind } from 'fym-shared/src/music24/coverRedo';
import { TypedMemo } from '../../../../../common/types';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import { useUpgradePrompt } from '../../../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../../config/music24PlanCapabilities';
import { studioThemeMessages } from '../../../studio/studioThemeMessages';
import { kindsToMake } from './useCoverRedoDialog';
import type { CoverRedoDialogModel } from './useCoverRedoDialog';
import { KIND_GROUP } from './coverRedoKindText';
import { coverRedoMessages as messages } from './coverRedoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover redo; each part uses its own slice of it */
import styles from './CoverRedo.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The thumbnails of a group row: at most 3. */
const GROUP_THUMBS = 3;

const NOTE: Partial<Record<CoverRedoKind, typeof messages.groupImagesNote>> = {
  images: messages.groupImagesNote,
  coverSizes: messages.groupCoverSizesNote,
  linkPreview: messages.groupLinkPreviewNote,
  aiReels: messages.groupAiReelsNote,
};

/** One group row (b1 `.rdg`): the tick, the name with the count, the thumbnails and the credits. */
const GroupRow: React.FC<{ kind: CoverRedoKind; items: CoverRedoItem[]; model: CoverRedoDialogModel }> = ({
  kind,
  items,
  model,
}) => {
  const intl = useIntl();
  const isOn = model.ticked.includes(kind);
  const credits = items.reduce((sum, item) => sum + item.credits, 0);
  const note = NOTE[kind];
  const names = [...new Set(items.map((item) => intl.formatMessage(studioThemeMessages[item.themeId].name)))];
  const detail = note === undefined ? names.join(', ') : intl.formatMessage(note);
  const thumbs = items.map((item) => item.thumbUrl).filter((url): url is string => url !== null);
  const checkId = useId();
  return (
    <label
      htmlFor={checkId}
      className={clsx(styles.group, !isOn && styles.groupOff)}
      data-testid={`cover-redo-group-${kind}`}
    >
      <input
        id={checkId}
        type="checkbox"
        className={styles.check}
        checked={isOn}
        onChange={() => model.toggleKind(kind)}
      />
      <span className={styles.groupName}>
        <b>{intl.formatMessage(KIND_GROUP[kind])}</b>
        <small>
          {items.length} · {detail}
        </small>
      </span>
      <span className={styles.thumbs} aria-hidden="true">
        {thumbs.slice(0, GROUP_THUMBS).map((url) => (
          <img key={url} className={styles.thumb} src={getFullAPIFileURL(url)} alt="" />
        ))}
      </span>
      <span className={clsx(styles.groupCost, credits === 0 && styles.groupFree)}>
        {credits === 0 ? intl.formatMessage(messages.groupFree) : intl.formatMessage(messages.credits, { credits })}
      </span>
    </label>
  );
};

/** One file that the redo leaves out, with its reason (b2 `.rds`). */
const SkipLine: React.FC<{ item: CoverRedoItem }> = ({ item }) => {
  const intl = useIntl();
  const openUpgrade = useUpgradePrompt();
  const name = intl.formatMessage(studioThemeMessages[item.themeId].name);
  let reason: string;
  switch (item.skipReason) {
    case 'needsPlan':
      reason = intl.formatMessage(
        needsFullVideoPlan(item.themeId) ? messages.skipNeedsPlan : messages.skipNeedsPlanAiFootage
      );
      break;
    case 'needsMaster':
      reason = intl.formatMessage(messages.skipNeedsMaster);
      break;
    case 'masterProcessing':
      reason = intl.formatMessage(messages.skipMasterProcessing);
      break;
    case 'busy':
      reason = intl.formatMessage(messages.skipBusy, { name });
      break;
    case 'failed':
    case null:
      reason = intl.formatMessage(messages.skipFailed, { name });
      break;
  }
  const showsName = item.skipReason !== 'busy' && item.skipReason !== 'failed';
  return (
    <div className={styles.skip}>
      <span>
        {showsName && <b>{name} · </b>}
        {reason}
      </span>
      {item.skipReason === 'needsPlan' && (
        <button type="button" className={styles.link} onClick={() => openUpgrade(SMLUpgradeFeature.generic, name)}>
          {intl.formatMessage(messages.skipSeePro)}
        </button>
      )}
    </div>
  );
};

/** "What we make again" (b1, b2): a row per group with a file, then the files that are left out. */
const CoverRedoGroupsComponent: React.FC<{ model: CoverRedoDialogModel }> = ({ model }) => {
  const intl = useIntl();
  const { state } = model;
  const groups = useMemo(() => {
    if (state === undefined) {
      return [];
    }
    return kindsToMake(state).map((kind) => ({
      kind,
      items: state.items.filter((item) => item.kind === kind && item.skipReason === null),
    }));
  }, [state]);
  const skipped = useMemo(() => (state?.items ?? []).filter((item) => item.skipReason !== null), [state]);
  return (
    <div className={styles.section}>
      <div className={styles.eyebrow}>
        <span>{intl.formatMessage(messages.whatWeMake)}</span>
        <span>{intl.formatMessage(messages.fileCount, { count: model.count })}</span>
      </div>
      {groups.map((group) => (
        <GroupRow key={group.kind} kind={group.kind} items={group.items} model={model} />
      ))}
      {skipped.map((item) => (
        <SkipLine key={item.uuid} item={item} />
      ))}
    </div>
  );
};

export const CoverRedoGroups = TypedMemo(CoverRedoGroupsComponent);
